import { MoreIcon } from "@/components/ui/icons/MoreIcon";
import { renderTextWithMentions } from "@/utils/mentionHighlight";
import { ChevronUp, LikeFilledIcon, LikeIcon } from "@roman/shared/components/Icons";
import {
  convertMentionsToUserIds,
  convertUserIdsToMentions,
  extractMentionsFromText,
} from "@roman/shared/features/comment/mentionParser";
import { useEditComment } from "@roman/shared/hooks/PredictionMarket/useEditComment";
import { useGetChildComments } from "@roman/shared/hooks/PredictionMarket/useGetChildComments";
import { useReportComment } from "@roman/shared/hooks/PredictionMarket/useReportComment";
import { useAuthStore } from "@roman/shared/store/auth.store";
import type { ParentPredictionMarketComment, ReportReason } from "@roman/shared/types/Comment";
import type { PredictionMarketChoice } from "@roman/shared/types/PredictionMarket";
import { formatRelativeTimestamp } from "@roman/shared/utils";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChildComment } from "./ChildComment";
import { CommentInput } from "./CommentInput";
import { CommentMenuBottomSheet } from "./CommentMenuBottomSheet";
import { CommentReportModal } from "./CommentReportModal";
import { EntryPointsDropdown } from "./EntryPointsDropdown";
import { LoadMoreButton } from "./LoadMoreButton";

type ParentCommentProps = {
  comment: ParentPredictionMarketComment;
  isReply?: boolean;
  refetchComments?: () => void;
  onShowLoginModal?: () => void;
  choices: PredictionMarketChoice[];
  onlyEntryAuthor: boolean;
};

export const ParentComment = ({
  comment,
  isReply = false,
  refetchComments,
  onShowLoginModal,
  choices,
  onlyEntryAuthor,
}: ParentCommentProps) => {
  const [showReplies, setShowReplies] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [shouldFetchReplies, setShouldFetchReplies] = useState(false);
  const [isEditingInline, setIsEditingInline] = useState(false);
  const [editText, setEditText] = useState(comment.text);
  const user = useAuthStore((state) => state.user);

  // Create mention mapping for this comment
  const userIdToNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of comment.mentionedUsers) {
      map.set(user.id, user.name);
    }
    return map;
  }, [comment.mentionedUsers]);

  const nameToUserIdMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of comment.mentionedUsers) {
      map.set(user.name, user.id);
    }
    return map;
  }, [comment.mentionedUsers]);

  // Convert <@userId> to @userName for display
  const displayText = useMemo(() => {
    return convertUserIdsToMentions(comment.text, userIdToNameMap);
  }, [comment.text, userIdToNameMap]);

  // Get mentioned names for highlighting
  const mentionedNames = useMemo(() => {
    return comment.mentionedUsers.map((user) => user.name);
  }, [comment.mentionedUsers]);

  // Calculate total entry points and get choice names
  const { totalEntryPoints, entryChoices } = useMemo(() => {
    const totalPoints = comment.author.entries.reduce((total, entry) => total + Number(entry.freePoints), 0);
    const entryChoices = comment.author.entries.map((entry) => {
      const choice = choices.find((c) => c.id === entry.predictionMarketChoiceId);
      return {
        choiceName: choice?.name || "不明",
        freePoints: Number(entry.freePoints),
        choiceId: entry.predictionMarketChoiceId,
      };
    });
    return { totalEntryPoints: totalPoints, entryChoices };
  }, [comment.author.entries, choices]);

  const { id: commentId, predictionMarketId } = comment;
  const {
    data: childComments,
    loading: loadingReplies,
    error: childCommentsError,
    loadMore: loadMoreChildComments,
    refetch: refetchChildComments,
    canLoadMore: canLoadMoreChildComments,
  } = useGetChildComments({ predictionMarketId, commentId }, onlyEntryAuthor, {
    initialPerPage: 10,
    loadMoreButton: true,
    shouldFetch: shouldFetchReplies,
  });

  const { reportComment, isMutating: _isReporting } = useReportComment({
    onSuccess: () => {
      setIsReportModalOpen(false);
      toast.success("コメントを報告しました");
    },
    onError: (error) => {
      console.error("Failed to report comment:", error);
      toast.error("コメントの報告に失敗しました");
    },
  });

  const { editComment, isMutating: isEditingComment } = useEditComment({
    onSuccess: () => {
      setIsEditingInline(false);
      // Use targeted update if available, otherwise fall back to full refresh
      refetchComments?.();
      toast.success("コメントを編集しました");
    },
    onError: (error) => {
      console.error("Failed to edit comment:", error);
      toast.error("コメントの編集に失敗しました");
    },
  });

  const handleToggleReplies = () => {
    const nextShowReplies = !showReplies;
    setShowReplies(nextShowReplies);

    // Start fetching when expanding for the first time
    if (nextShowReplies && !shouldFetchReplies) {
      setShouldFetchReplies(true);
    }
  };

  const handleMenuClick = () => {
    setIsMenuOpen(true);
  };

  const handleReport = () => {
    setIsReportModalOpen(true);
  };

  const handleReportSubmit = (reason: ReportReason) => {
    if (!user) {
      onShowLoginModal?.();
      return;
    }
    reportComment({
      predictionMarketId: comment.predictionMarketId,
      commentId: comment.id,
      reason,
    });
  };

  const handleDelete = () => {
    refetchComments?.();
  };

  // Update editText when comment changes - use the display text for editing
  useEffect(() => {
    setEditText(displayText);
  }, [displayText]);

  const handleStartInlineEdit = () => {
    setIsEditingInline(true);
  };

  const handleSaveInlineEdit = () => {
    const trimmedText = editText.trim();
    if (trimmedText && trimmedText.length >= 1 && trimmedText.length <= 500) {
      // Convert @mentions back to <@userId> format before saving
      const textWithUserIds = convertMentionsToUserIds(trimmedText, nameToUserIdMap);
      // Extract mentioned user IDs from the text
      const mentionedUserIds = extractMentionsFromText(trimmedText, nameToUserIdMap);

      editComment({
        predictionMarketId: comment.predictionMarketId,
        commentId: comment.id,
        text: textWithUserIds,
        mentionedUserIds,
      });
    }
  };

  const handleCancelInlineEdit = () => {
    setIsEditingInline(false);
    setEditText(displayText);
  };

  return (
    <div id={`comment-${comment.id}`} className={isReply ? "flex gap-[10px] mt-[15px]" : "flex gap-[10px]"}>
      <div className="relative">
        <img
          src={comment.author.avatar || "/assets/usericon.svg"}
          alt="avatar"
          className={isReply ? "w-8 h-8 rounded-full object-cover" : "w-[36px] h-[36px] rounded-full object-cover"}
        />

        {comment.numberOfReplies !== 0 && (
          <div
            className="border-l border-gray-100 absolute top-[44px] left-[18px] z-0"
            style={{ height: "calc(100% - 44px)" }}
          />
        )}
      </div>
      <div className="flex-1">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="font-bold text-xs text-gray font-noto">{comment.author.name}</span>
              {totalEntryPoints > 0 && <EntryPointsDropdown entryChoices={entryChoices} choices={choices} />}
            </div>
            <span className="text-[10px] font-medium text-light-gray font-noto">
              {formatRelativeTimestamp(new Date(comment.commentedAt).getTime())}
            </span>
          </div>
          <button
            type="button"
            onClick={handleMenuClick}
            className="p-2 md:hover:bg-light-gray/10 active:bg-light-gray/10 rounded-lg transition-colors"
          >
            <MoreIcon size={16} color="#808080" />
          </button>
        </div>
        <div className="mt-[11px]">
          {isEditingInline ? (
            <CommentInput
              setSearch={setEditText}
              onSubmit={handleSaveInlineEdit}
              onCancel={handleCancelInlineEdit}
              isSubmitting={isEditingComment}
              autoFocus={true}
              isEdit={true}
              initialValue={displayText}
              mention={mentionedNames}
            />
          ) : (
            <div className="text-[12px] font-normal text-black">
              {displayText.split("\n").map((line: string, idx: number) => (
                <span key={idx}>
                  {renderTextWithMentions(line, mentionedNames)}
                  <br />
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-[21px] mt-4">
          <div
            className={`flex items-center justify-center gap-[6px] ${"cursor-not-allowed opacity-50"}`}
            onClick={() => {
              window.alert("本機能はモバイルアプリでのみ利用が可能です。");
            }}
            style={{
              color: "#808080",
            }}
          >
            {comment.isLikedByMe ? <LikeFilledIcon /> : <LikeIcon />}
            <div
              className="text-[10px] font-medium font-noto"
              style={{
                color: "#808080",
              }}
            >
              {comment.numberOfLikes}
            </div>
          </div>
        </div>
        {comment.numberOfReplies !== 0 && (
          <div
            className="mt-[20px] h-[14px] font-bold text-[10px] text-secondary font-noto cursor-pointer select-none flex items-center gap-1 relative"
            onClick={handleToggleReplies}
          >
            {!showReplies &&
              Array.from({ length: Math.min(3, comment.numberOfReplies) }).map((_, index) => {
                const left = -34.5 + index * 8;
                const zIndex = 4 - index;
                const color = index % 3 === 0 ? "#00E218" : index % 3 === 1 ? "#D5AB2A" : "#91E9FF";
                return (
                  <div
                    key={index}
                    className={"w-[14px] h-[14px] rounded-full absolute top-0"}
                    style={{ left: `${left}px`, zIndex, backgroundColor: color }}
                  />
                );
              })}
            <span>{`${comment.numberOfReplies}件の返信`}</span>
            <ChevronUp className={`w-3 h-3 ${showReplies ? "rotate-0" : "rotate-180"}`} />
          </div>
        )}
        {showReplies && (
          <div className="">
            {loadingReplies ? (
              <div className="text-xs text-gray">Loading...</div>
            ) : (
              <>
                {/* Load More Button for Child Comments */}
                <div className="relative">
                  <div className="h-full w-[1px] absolute top-[0px] left-[-28px] z-10 bg-white" />
                  <LoadMoreButton
                    onLoadMore={loadMoreChildComments}
                    loading={loadingReplies}
                    hasMore={canLoadMoreChildComments}
                    error={childCommentsError}
                    onRetry={loadMoreChildComments}
                  />
                </div>
                {childComments
                  .filter((reply) => !reply.isDeleted)
                  .sort((a, b) => new Date(a.commentedAt).getTime() - new Date(b.commentedAt).getTime()) // Sort by oldest first
                  .map((reply, index, array) => (
                    <div className="relative" key={`${reply.id}-${index}`}>
                      {/* Only round the bottom-left corner */}
                      {index === array.length - 1 && (
                        <div className="h-full w-[1px] absolute top-[0px] left-[-28px] bg-white" />
                      )}
                      <div className="h-[26px] w-[27px] rounded-bl-[10px] absolute top-[0px] left-[-28px] border-l border-b border-gray-100" />

                      <ChildComment
                        key={reply.id}
                        comment={reply}
                        isReply
                        onShowLoginModal={onShowLoginModal}
                        choices={choices}
                        refetch={refetchChildComments}
                      />
                    </div>
                  ))}
              </>
            )}
          </div>
        )}
      </div>

      <CommentMenuBottomSheet
        open={isMenuOpen}
        setOpen={setIsMenuOpen}
        comment={comment}
        onReport={handleReport}
        onDeleteSuccess={handleDelete}
        onStartInlineEdit={handleStartInlineEdit}
      />

      <CommentReportModal
        open={isReportModalOpen}
        setOpen={setIsReportModalOpen}
        commentId={comment.id}
        onReportSubmit={handleReportSubmit}
      />
    </div>
  );
};
